import React, { useState } from "react";
import {
  Row,
  Col,
  Drawer,
  Button,
  Select,
  Form,
  Tooltip,
  Checkbox,
  Input,
  notification,
} from "antd";
import styles from "./MemberProfile.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import drawerStyles from "../TripDetails/TripDetailsDrawer.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import { IServiceUserInfo } from "../../../types/ServiceUserInfo";
import ProfilePhoto from "./ProfileForm/ProfilePhoto";
import ProfileDateOfBirth from "./ProfileForm/ProfileDateOfBirth";
import { selectDropdownArrowIcon } from "../../../assets/images";
import { isEmailValid } from "../../../helpers/ValidationHelpers";
import { InputMask } from "@mtm/components";
import ProfileAddress from "./ProfileForm/ProfileAddress";
import { useUspsValidator } from "../../../hooks/useUspsValidator";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import { useProfileActions } from "../../../hooks/useProfileActions";
import { defaultSelectedAddress } from "../../../types/LocationTypes";
import { useProfileForm } from "../../../hooks/useProfileForm";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import UnsavedChangesModal from "../../CreatePerson/Modals/UnsavedChangesModal";
import InvalidEmailModal from "../../CreatePerson/Modals/InvalidEmailModal";
import UspsAddressModal from "../../CreatePerson/Modals/UspsAddressModal";
import ProfileAccommodations from "./ProfileForm/ProfileAccommodations";
import deleteIcon from "../../../assets/icons/deleteRedFilled.svg";
import RestrictedServiceUserRemovalModal from "./Modals/RestrictedServiceUserRemovalModal";
import moment from "moment";
import { getWandaTripWithPagination } from "../../../services";
import {
  RegistrationEnums,
  WandaTripStatus,
} from "../../../types/ApplicationTypes";
import RemoveServiceUserModal from "./Modals/RemoveServiceUserModal";
import { DeleteServiceUser } from "../../../services/ProfilingService";
import { useDashboardCtx } from "../../../context/DashboardContext";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";

type Props = {
  profile: IServiceUserInfo;
  isVisible: boolean;
  setVisibility: Function;
  updateViewProfileDetails: Function;
  closeServiceUserProfileDrawers: Function;
};

function EditProfileDrawer({
  profile,
  isVisible,
  setVisibility,
  updateViewProfileDetails,
  closeServiceUserProfileDrawers,
}: Props) {
  const [
    isUnsavedChangesModalVisible,
    setIsUnsavedChangesModalVisible,
  ] = useState<boolean>(false);
  const [isInvalidEmailModalVisible, setIsInvalidEmailModalVisible] = useState<
    boolean
  >(false);
  const [
    isRestrictedServiceUserRemovalModalVisible,
    setIsRestrictedServiceUserRemovalModalVisible,
  ] = useState<boolean>(false);
  const [
    isRemoveServiceUserModalVisible,
    setIsRemoveServiceUserModalVisible,
  ] = useState<boolean>(false);

  const [form] = Form.useForm();

  const { relationshipTypesCtx } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  const { selectedPersonIdCtx } = useDashboardCtx();
  const [, setSelectedPersonId] = selectedPersonIdCtx;

  const { serviceDataCtx, wandaOrgId } = useTripsCtx();
  const [serviceData, setServiceData] = serviceDataCtx;

  const { tripDetailsInfo } = usePlanDetailsCtx();

  const { initialValues, isLoadingProfile } = useProfileForm(profile.personId);

  const { caregiverSummaryInfo } = usePersonSummaryCtx();

  function navigateOnSubmitForm(serviceUsersList: IServiceUserInfo[]) {
    setVisibility(false);
    updateViewProfileDetails(profile.personId, serviceUsersList);
  }

  const {
    shouldDisableBtn,
    validateForm,
    profileInfo,
    handleSubmitForm,
    isLoading,
  } = useProfileActions(
    form,
    profile.personId,
    initialValues,
    setIsInvalidEmailModalVisible,
    navigateOnSubmitForm
  );

  const {
    isCaregiverProfile,
    isActiveServiceUser,
    isEmailDisabled,
  } = profileInfo;

  const {
    addressRef,
    uspsAddresses,
    handleUspsValidationAndSubmitForm,
    isUspsAddressModalVisible,
    setIsUspsAddressModalVisible,
  } = useUspsValidator(form, handleSubmitForm);

  function handleOnCloseEditProfile() {
    setVisibility(false);
  }

  function handleValuesChange() {
    validateForm();
  }

  function handleIsSameAsHomeAddress(isChecked: boolean) {
    if (isChecked) {
      const address = form.getFieldValue("homeAddress");
      form.setFieldsValue({
        mailingAddress: address,
      });
    } else {
      form.setFieldsValue({
        mailingAddress: defaultSelectedAddress,
      });
    }
    validateForm();
  }

  async function handleDeleteServiceUser() {
    try {
      const currentDate = moment().format("MM/DD/YYYY");
      const futureDate = moment()
        .add(12, "months")
        .format("MM/DD/YYYY");

      let trips = await getWandaTripWithPagination({
        personId: profile.personId,
        wandaTripStatusIds: WandaTripStatus.Booked,
        skipPagination: true,
        fromDate: currentDate,
        toDate: futureDate,
        selectedPersonId: String(profile.personId),
      });

      if (trips.totalTripCount > 0)
        setIsRestrictedServiceUserRemovalModalVisible(true);
      else setIsRemoveServiceUserModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Failed to Delete Service User",
      });
    }
  }

  async function handleRemoveServiceUser() {
    try {
      const data = {
        organizationId: wandaOrgId.benefitGroupId,
        personId: profile.personId,
        personTypeId: RegistrationEnums.PersonTypeId,
        isActive: false,
        relationshipRefID: RegistrationEnums.PersonToBenefitRel,
      };
      await DeleteServiceUser(data, caregiverSummaryInfo.personId);

      const filteredServiceData = serviceData.filter(
        (serviceUser: { personId: number }) =>
          serviceUser.personId !== data.personId
      );
      setServiceData(filteredServiceData);
      setSelectedPersonId(
        filteredServiceData.length === 1 ? filteredServiceData[0]?.personId : ""
      );

      setIsRemoveServiceUserModalVisible(false);
      closeServiceUserProfileDrawers();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to delete service user. Please try again later.",
      });
    }
  }

  return (
    <>
      <Drawer
        placement="right"
        className={drawerStyles.drawerContainer}
        onClose={handleOnCloseEditProfile}
        visible={isVisible}
      >
        {isLoadingProfile ? (
          <Spinner />
        ) : (
          <Form
            form={form}
            name="editProfile"
            labelCol={{ span: 24 }}
            initialValues={initialValues}
            onFinish={handleUspsValidationAndSubmitForm}
            className={`${styles.editProfileForm} ${styles.padding24}`}
            onValuesChange={handleValuesChange}
            requiredMark={false}
          >
            <Row
              justify="center"
              align="middle"
              className={styles.profileHeader}
            >
              <ProfilePhoto form={form} validateForm={validateForm} />
              <Col className={styles.memberName}>
                {profile.firstName} {profile.lastName}
              </Col>
            </Row>

            <Row className={styles.profileBody}>
              <Col
                span={24}
                className={`${textStyles.boldText16} ${styles.marginDown8}`}
              >
                Member Information
              </Col>
              <Col span={isCaregiverProfile ? 24 : 11}>
                <ProfileDateOfBirth form={form} />
              </Col>
              <Col span={isCaregiverProfile ? 0 : 1} />
              {!isCaregiverProfile && (
                <Col span={12}>
                  <Form.Item
                    name="relationship"
                    label="Relationship"
                    className={styles.formItemAlign}
                  >
                    <Select
                      className={styles.relationshipSelect}
                      placeholder="Relationship"
                      suffixIcon={
                        <img
                          src={selectDropdownArrowIcon}
                          alt="select_reason_dropdown"
                        />
                      }
                      options={relationshipTypes}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={24}>
                <Form.Item
                  name="phone"
                  label="Phone number"
                  className={styles.formItemAlign}
                >
                  <InputMask
                    className={styles.input}
                    mask="(999) 999-9999"
                    maskPlaceholder=""
                    placeholder="(555) 123-4567"
                    onChange={() => {}}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Tooltip
                  title={
                    isEmailDisabled && (
                      <Row>
                        Please contact us at{" "}
                        {tripDetailsInfo.wandaCustomerCareNumber} to change this
                        information.
                      </Row>
                    )
                  }
                  overlayClassName={styles.tooltip}
                >
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: false, message: "" },
                      {
                        validator(_, value) {
                          if (!value || isEmailValid(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject("");
                        },
                      },
                    ]}
                    className={""}
                  >
                    <Input
                      placeholder="Email"
                      className={`${styles.input} ${isEmailDisabled &&
                        styles.disabledInput}`}
                      disabled={isEmailDisabled}
                    />
                  </Form.Item>
                </Tooltip>
              </Col>

              <Col
                span={24}
                className={`${textStyles.normalText14} ${styles.formLabel}`}
              >
                Home address
              </Col>
              <Col span={24}>
                <ProfileAddress
                  ref={addressRef.current[0]}
                  form={form}
                  addressType="homeAddress"
                  validateAndCheckForm={validateForm}
                  isCaregiverProfile={isCaregiverProfile}
                  shouldValidateZip={isActiveServiceUser}
                />
              </Col>

              <Col
                span={24}
                className={`${textStyles.normalText14} ${styles.formLabel}`}
              >
                Mailing address
              </Col>
              <Col span={24}>
                <Row gutter={[10, 10]} align="middle">
                  <Col>
                    <Form.Item
                      name="isSameAsHomeAddress"
                      className={styles.marginDown0}
                      valuePropName="checked"
                    >
                      <Checkbox
                        className={styles.checkbox}
                        onChange={(e) =>
                          handleIsSameAsHomeAddress(e.target.checked)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col className={textStyles.normalText14}>
                    Same as home address
                  </Col>
                  <Col span={24} />
                </Row>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={styles.marginDown0}
                  dependencies={["isSameAsHomeAddress"]}
                >
                  {({ getFieldValue }) =>
                    !getFieldValue("isSameAsHomeAddress") && (
                      <ProfileAddress
                        ref={addressRef.current[1]}
                        form={form}
                        addressType="mailingAddress"
                        validateAndCheckForm={validateForm}
                        isCaregiverProfile={isCaregiverProfile}
                        shouldValidateZip={isActiveServiceUser}
                      />
                    )
                  }
                </Form.Item>
              </Col>

              {isActiveServiceUser && (
                <>
                  <Col
                    span={24}
                    className={`${textStyles.boldText16} ${styles.formLabel}`}
                  >
                    Accommodations
                  </Col>
                  <Col span={24}>
                    <ProfileAccommodations
                      form={form}
                      accommodationType="wandaAccommodations"
                    />
                  </Col>
                </>
              )}

              <Col span={24}>
                <Row justify="center" gutter={[0, 72]}>
                  <Button
                    onClick={handleDeleteServiceUser}
                    className={buttonStyles.deleteRedBtn16}
                  >
                    <img src={deleteIcon} alt="delete_service_user" />
                    Delete service user
                  </Button>
                  <Col span={24} />
                </Row>
              </Col>
            </Row>

            <Row
              justify="end"
              className={buttonStyles.drawerFooterBtnsContainer}
              gutter={[16, 16]}
            >
              <Col>
                <Button
                  className={`${buttonStyles.defaultWhiteBtn} ${textStyles.normalText16}`}
                  onClick={handleOnCloseEditProfile}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  htmlType="submit"
                  loading={isLoading}
                  disabled={shouldDisableBtn}
                  className={`${buttonStyles.primaryBlueBtn} ${
                    textStyles.normalText16
                  } ${shouldDisableBtn && styles.disableBtn}`}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Drawer>

      <UnsavedChangesModal
        visible={isUnsavedChangesModalVisible}
        setVisibility={setIsUnsavedChangesModalVisible}
      />

      <InvalidEmailModal
        visible={isInvalidEmailModalVisible}
        setVisibility={setIsInvalidEmailModalVisible}
      />

      <UspsAddressModal
        form={form}
        uspsAddresses={uspsAddresses.current}
        visible={isUspsAddressModalVisible}
        setVisibility={setIsUspsAddressModalVisible}
        handleSubmitForm={handleSubmitForm}
      />

      <RestrictedServiceUserRemovalModal
        serviceUserName={`${profile.firstName} ${profile.lastName}`}
        visible={isRestrictedServiceUserRemovalModalVisible}
        setVisibility={setIsRestrictedServiceUserRemovalModalVisible}
      />

      <RemoveServiceUserModal
        serviceUserName={`${profile.firstName} ${profile.lastName}`}
        visible={isRemoveServiceUserModalVisible}
        setVisibility={setIsRemoveServiceUserModalVisible}
        handleRemoveServiceUser={handleRemoveServiceUser}
      />
    </>
  );
}

export default EditProfileDrawer;
